import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, CheckCircle2, AlertTriangle, HelpCircle, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { formatDate } from "@/lib/nav";
import { useT52 } from "@/i18n/v52";

// Echte Server-Prüfungen aus /api/system/status; Ungeprüftes = grau „Nicht geprüft“, keine Zahlen.
const STATE = {
  ok: { icon: CheckCircle2, cls: "text-status-success" },
  fail: { icon: AlertTriangle, cls: "text-status-warning" },
  unknown: { icon: HelpCircle, cls: "text-fundio-muted" },
};

function Card({ label, value, testid }) {
  return (
    <div className="card-surface rounded-xl p-5" data-testid={testid}>
      <div className="text-xs text-fundio-faint">{label}</div>
      <div className="mt-1.5 text-sm font-medium text-fundio-ink">{value}</div>
    </div>
  );
}

export default function SystemStatus() {
  const { t52 } = useT52();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    setData(null);
    api.get("/system/status").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (data === null) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>;
  const recheck = (
    <button type="button" onClick={load} data-testid="system-status-page-recheck" className="inline-flex items-center gap-2 rounded-lg border border-fundio-border bg-white px-3 py-2 text-sm font-medium text-fundio-ink hover:bg-fundio-subtle">
      <RefreshCw className="h-4 w-4" />{t52("stRecheck")}
    </button>
  );
  if (data === false)
    return (
      <div data-testid="system-status-page">
        <PageHeader title={t52("status")} subtitle={t52("spLead")}>{recheck}</PageHeader>
        <p data-testid="system-status-error" className="card-surface rounded-xl p-5 text-sm text-status-warning">{t52("stError")}</p>
      </div>
    );

  const s = data.summary || {};
  return (
    <div data-testid="system-status-page">
      <PageHeader title={t52("status")} subtitle={t52("spLead")}>{recheck}</PageHeader>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card label={t52("spEnv")} value={data.environment} testid="system-status-env" />
        <Card label={t52("spResult")} testid="system-status-summary"
          value={`${s.ok ?? 0} ${t52("stOk")} · ${s.fail ?? 0} ${t52("stFail")} · ${s.unknown ?? 0} ${t52("stUnk")}`} />
        <Card label={t52("spLast")} value={formatDate(data.checked_at)} testid="system-status-checked" />
      </div>
      <Panel title={t52("spServices")} testid="system-services-panel">
        <ul className="divide-y divide-fundio-border">
          {(data.services || []).map((svc) => {
            const st = STATE[svc.status] || STATE.unknown;
            const Icon = st.icon;
            return (
              <li key={svc.key} data-testid={`system-service-${svc.key}`} className="flex items-center justify-between gap-4 px-3 py-4">
                <div className="flex items-center gap-3">
                  <Icon className={`h-4 w-4 shrink-0 ${st.cls}`} />
                  <div>
                    <div className="text-sm font-medium text-fundio-ink">{t52(`svc_${svc.key}`)}</div>
                    <div className="text-xs text-fundio-muted">{t52(`rs_${svc.reason}`)}</div>
                  </div>
                </div>
                <span className={`text-xs font-medium ${st.cls}`} data-testid={`system-service-${svc.key}-status`}>{t52(`chip_${svc.status}`)}</span>
              </li>
            );
          })}
        </ul>
      </Panel>
      <p className="mt-4 text-xs text-fundio-muted">{t52("stNote")}</p>
    </div>
  );
}
